import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, countProductsInGroup, thumbForGroup } from './catalogGroupUtils';
import { formatDisplayTitle } from './formatProductTitle';
import { categoryHeroBlurb, categoryHeroTagline } from './categoryHeroText';
import { useSiteSettings } from './SiteSettingsContext';
import CatalogImage from './catalog/CatalogImage';

const RAKHI_SLUG = 'rakhi';

const RakhiCollections = () => {
  const navigate = useNavigate();
  const { products, categories, loading } = useProducts();
  const { settings: site } = useSiteSettings();
  const taglineFallback = site.store_copy?.category_tagline_rakhi || 'Handcrafted rakhi for every bond';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const meta = useMemo(() => categories.find((c) => c.slug === RAKHI_SLUG), [categories]);
  const list = useMemo(() => products[RAKHI_SLUG] || [], [products]);

  const tiles = useMemo(() => buildCategoryGroupCards(meta, list), [meta, list]);

  const defaultHeroBlurb =
    list.length > 0
      ? `${list.length} design${list.length === 1 ? '' : 's'} — choose a style below to browse.`
      : 'New pieces are added often — please check back soon.';

  if (loading) {
    return (
      <div className="collections-page">
        <div className="container py-5 text-center text-muted">Loading Rakhi collections…</div>
      </div>
    );
  }

  return (
    <div className="collections-page">
      <div className="container store-page__container">
        <section className="collections-hero">
          <div className="collections-hero-panel text-center">
            <h1 className="collections-page-title">{formatDisplayTitle(meta?.name || 'Rakhi')}</h1>
            <p className="collections-hero-tagline">
              {categoryHeroTagline(meta, taglineFallback)}
            </p>
            <p className="collections-page-subtitle handwritten">
              {categoryHeroBlurb(meta, defaultHeroBlurb)}
            </p>
          </div>
        </section>

        <section className="collections-grid-section">
          {tiles.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-3">Collections will appear here as soon as styles are available.</p>
              <Link to={`/shop/${RAKHI_SLUG}`} className="btn btn-primary me-2">
                View category hub
              </Link>
              <Link to="/shop" className="btn btn-outline-secondary">
                Back to shop
              </Link>
            </div>
          ) : (
            <div className="row g-4">
              {tiles.map((type) => {
                const n = countProductsInGroup(list, type.key);
                const img = thumbForGroup(list, type.key);
                return (
                  <div className="col-6 col-md-4 col-lg-3" key={type.key}>
                    <div
                      className="collection-type-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/shop/${RAKHI_SLUG}/${encodeURIComponent(type.key)}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/shop/${RAKHI_SLUG}/${encodeURIComponent(type.key)}`);
                        }
                      }}
                    >
                      <div className="type-image-wrapper">
                        <CatalogImage
                          src={img}
                          alt={type.label}
                          className="type-image"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                        <div className="type-overlay">
                          <span className="view-collection">View Collection</span>
                        </div>
                      </div>
                      <div className="type-content">
                        <h3 className="type-title">{type.label}</h3>
                        <p className="type-desc">{n > 0 ? `${n} design${n === 1 ? '' : 's'}` : 'Coming soon'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RakhiCollections;
